import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { CustomColumnDef } from "@/components/data-table/types/custom-column-def";
import Conversation from "@/models/conversation/Conversation";
import { DateTime } from "luxon";
import { User } from "@/models/users/User";
import { PiiOverrideConversationSummary } from "@/models/pii/PiiOverride";
import { ShieldAlert } from "lucide-react";
import ConversationDeleteAction from "./ConversationDeleteAction";

/**
 * PII-override badge column (LAR-27 follow-up). Appended only while the
 * PII-Redaction flag is on (ConversationTable passes no map otherwise), and a
 * row only shows the badge when the batch summary reports ≥1 override audit.
 */
const piiOverrideColumn = (
  piiOverrideSummaries: Map<string, PiiOverrideConversationSummary>
): CustomColumnDef<Conversation> => ({
  id: "piiOverride",
  header: () => <span className="text-xs">PII</span>,
  enableSorting: false,
  cell: ({ row }: { row: { original: Conversation } }) => {
    const summary = piiOverrideSummaries.get(row.original.id);
    if (!summary) return null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 text-warning">
            <ShieldAlert
              className="size-4"
              aria-label="PII-anonymisering tilsidesat"
            />
            <span className="text-xs">{summary.overrideCount}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          PII-anonymisering tilsidesat
          {summary.hardIdOverridden
            ? " — heraf CPR-numre, telefonnumre, e-mailadresser eller kontonumre"
            : ""}
        </TooltipContent>
      </Tooltip>
    );
  },
  meta: {
    displayName: "PII",
    dataType: "text",
  },
});

/**
 * Admin delete column (LAR-367 #4). Appended only for a viewer who holds
 * DeleteAnyConversation, so nobody is shown a column they can never act on; an
 * already-deleted conversation renders no button, matching the drill-down.
 */
const ACTIONS_HEADER = "Handlinger";

const deleteColumn = (): CustomColumnDef<Conversation> => ({
  id: "delete",
  header: () => <span className="text-xs">{ACTIONS_HEADER}</span>,
  enableSorting: false,
  cell: ({ row }: { row: { original: Conversation } }) =>
    row.original.isDeleted ? null : (
      <ConversationDeleteAction
        conversationId={row.original.id}
        conversationTitle={row.original.title}
      />
    ),
  meta: {
    displayName: ACTIONS_HEADER,
  },
});

export const getConversationColumns = (
  users: User[] | undefined,
  piiOverrideSummaries?: Map<string, PiiOverrideConversationSummary>,
  canDeleteConversations?: boolean
): CustomColumnDef<Conversation>[] => [
  {
    accessorKey: "title",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Titel" className="ml-2" />
    ),
    cell: ({ row }: { row: { original: Conversation } }) => (
      <div className="ml-2">{row.original.title || "Uden titel"}</div>
    ),
    meta: {
      displayName: "Titel",
      dataType: "text",
    },
  },
  {
    accessorKey: "userId",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Oprettet af" />
    ),
    cell: ({ row }: { row: { original: Conversation } }) => {
      const user = users?.find((u) => u.id === row.original.userId);
      return user ? `${user.firstName} ${user.lastName}` : "Ukendt bruger";
    },
    meta: {
      displayName: "Oprettet af",
      dataType: "text",
    },
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Oprettet" />
    ),
    cell: ({ row }: { row: { original: Conversation } }) =>
      DateTime.fromISO(row.original.createdAt)
        .setLocale("da-dk")
        .toLocaleString(DateTime.DATETIME_MED),
    meta: {
      displayName: "Oprettet",
      dataType: "date",
    },
  },
  {
    accessorKey: "messageCount",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Antal beskeder" />
    ),
    cell: ({ row }: { row: { original: Conversation } }) =>
      row.original.messageCount,
    meta: {
      displayName: "Antal beskeder",
      dataType: "number",
    },
  },
  {
    accessorKey: "isWordConversation",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    cell: ({ row }: { row: { original: Conversation } }) => (
      <Badge
        variant={
          row.original.isWordConversation
            ? "secondary"
            : row.original.searchOptions.length > 0
              ? "default"
              : "outline"
        }
      >
        {row.original.isWordConversation
          ? "Word"
          : row.original.searchOptions.length > 0 &&
              row.original.searchOptions[0].type !== "ChatGPT"
            ? `Chat - ${row.original.searchOptions[0].type}`
            : "Chat uden filter"}
      </Badge>
    ),
    meta: {
      displayName: "Type",
      dataType: "text",
    },
  },
  {
    accessorKey: "isDeleted",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }: { row: { original: Conversation } }) => (
      <Badge variant={row.original.isDeleted ? "destructive" : "success"}>
        {row.original.isDeleted ? "Slettet" : "Aktiv"}
      </Badge>
    ),
    meta: {
      displayName: "Status",
      dataType: "text",
    },
  },
  ...(piiOverrideSummaries ? [piiOverrideColumn(piiOverrideSummaries)] : []),
  ...(canDeleteConversations ? [deleteColumn()] : []),
];
